import { useState, useCallback } from 'react';
import { getDefaultCommonAttrs, getDefaultSpecificAttrs } from '../utils/defaultAttributes';
import { getTheme } from '../config/themes';
import { getLayoutSlots, getLayout } from '../config/layouts';

export const usePageBuilder = () => {
  const [pageData, setPageData] = useState({
    layoutId: 'single-column',
    themeId: 'modern',
    components: [],
    metadata: {
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: '1.0.0'
    }
  });

  /**
   * Smart slot mapping when layout changes
   * Attempts to map components from old layout slots to new layout slots
   */
  const autoMapComponentsToSlots = useCallback((components, oldLayoutId, newLayoutId) => {
    const oldSlots = getLayoutSlots(oldLayoutId);
    const newSlots = getLayoutSlots(newLayoutId);

    // If no old slots (legacy), assign all to first content slot
    if (oldSlots.length === 0) {
      const firstContentSlot = newSlots.find(s => 
        s.id === 'content' || s.id === 'body' || s.id === 'main-content' || s.allowMultiple
      ) || newSlots[0];
      
      return components.map(comp => ({
        ...comp,
        slotId: firstContentSlot?.id || newSlots[0]?.id
      }));
    }

    // Map components to similar slots in new layout
    return components.map(comp => {
      const currentSlotId = comp.slotId;
      
      // Try to find matching slot by ID first
      if (newSlots.find(s => s.id === currentSlotId)) {
        return comp; // Slot exists in new layout, keep it
      }

      // Try to find slot by name similarity
      const oldSlot = oldSlots.find(s => s.id === currentSlotId);
      if (oldSlot) {
        const similarSlot = newSlots.find(s => 
          s.name === oldSlot.name || 
          s.id.includes(oldSlot.id.split('-')[0]) ||
          (s.suggestedComponents && oldSlot.suggestedComponents && 
           s.suggestedComponents.some(c => oldSlot.suggestedComponents.includes(c)))
        );
        
        if (similarSlot) {
          return { ...comp, slotId: similarSlot.id };
        }
      }

      // Fallback: assign to first compatible slot based on component type
      const compatibleSlot = newSlots.find(s => 
        s.suggestedComponents && s.suggestedComponents.includes(comp.type)
      );
      
      if (compatibleSlot) {
        return { ...comp, slotId: compatibleSlot.id };
      }

      // Final fallback: assign to first slot that allows multiple or first slot
      const flexSlot = newSlots.find(s => s.allowMultiple) || newSlots[0];
      return { ...comp, slotId: flexSlot?.id || 'content' };
    });
  }, []);

  const updateLayout = useCallback((layoutId) => {
    setPageData(prev => {
      const oldLayoutId = prev.layoutId;
      const componentsWithSlots = autoMapComponentsToSlots(
        prev.components,
        oldLayoutId,
        layoutId
      );

      return {
        ...prev,
        layoutId,
        components: componentsWithSlots,
        metadata: { 
          ...prev.metadata, 
          updatedAt: new Date().toISOString(),
          lastLayoutChange: {
            from: oldLayoutId,
            to: layoutId,
            timestamp: new Date().toISOString()
          }
        }
      };
    });
  }, [autoMapComponentsToSlots]);

  const updateTheme = useCallback((themeId) => {
    setPageData(prev => ({
      ...prev,
      themeId,
      metadata: { ...prev.metadata, updatedAt: new Date().toISOString() }
    }));
  }, []);

  const addComponent = useCallback((componentType, slotId = null, direction = 'column') => {
  const theme = getTheme(pageData.themeId);
  const slots = getLayoutSlots(pageData.layoutId);

  // fixed/updated by ChatGPT on 2026-01-26 18:25:00 – allow nested components (grid cell children) without assigning to a page slot
  const isNestedCreate = slotId === '__nested__';

  let targetSlotId = slotId;

  // fixed/updated by ChatGPT on 2026-01-26 18:25:00 – nested children must not go to any slot
  if (isNestedCreate) {
    targetSlotId = null;
  } else if (!targetSlotId && slots.length > 0) {
    const compatibleSlot = slots.find(s =>
      s.suggestedComponents && s.suggestedComponents.includes(componentType)
    );
    targetSlotId =
      compatibleSlot?.id ||
      slots.find(s => s.allowMultiple)?.id ||
      slots[0]?.id;
  }

  const newComponent = {
    name: `${componentType} component`,
    id: `${componentType}_${Date.now()}`,
    type: componentType,
    slotId: targetSlotId,
    direction,
    commonAttrs: getDefaultCommonAttrs(),
    specificAttrs: getDefaultSpecificAttrs(componentType, theme),
    createdAt: new Date().toISOString(),

    // fixed/updated by ChatGPT on 2026-01-26 18:25:00 – mark nested children explicitly
    ...(isNestedCreate ? { isNested: true } : {})
  };

  setPageData(prev => ({
    ...prev,
    components: [...prev.components, newComponent],
    metadata: { ...prev.metadata, updatedAt: new Date().toISOString() }
  }));

  return newComponent.id;
}, [pageData.themeId, pageData.layoutId]);

  const updateComponent = useCallback((componentId, updates) => {
    setPageData(prev => ({
      ...prev,
      components: prev.components.map(comp =>
        comp.id === componentId ? { ...comp, ...updates } : comp
      ),
      metadata: { ...prev.metadata, updatedAt: new Date().toISOString() }
    }));
  }, []);

  const deleteComponent = useCallback((componentId) => {
    setPageData(prev => ({
      ...prev,
      components: prev.components.filter(comp => comp.parentComponentId !== componentId && comp.id !== componentId),
      metadata: { ...prev.metadata, updatedAt: new Date().toISOString() }
    }));
  }, []);

  const reorderComponents = useCallback((startIndex, endIndex) => {
    setPageData(prev => {
      const result = Array.from(prev.components);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);

      return {
        ...prev,
        components: result,
        metadata: { ...prev.metadata, updatedAt: new Date().toISOString() }
      };
    });
  }, []);

  const duplicateComponent = useCallback((componentId) => {
    setPageData(prev => {
      const component = prev.components.find(c => c.id === componentId);
      if (!component) return prev;

      const duplicated = {
        ...component,
        id: `${component.type}_${Date.now()}`,
        createdAt: new Date().toISOString()
      };

      return {
        ...prev,
        components: [...prev.components, duplicated],
        metadata: { ...prev.metadata, updatedAt: new Date().toISOString() }
      };
    });
  }, []);

  return {
    pageData,
    setPageData,
    updateLayout,
    updateTheme,
    addComponent,
    updateComponent,
    deleteComponent,
    reorderComponents,
    duplicateComponent
  };
};
